import "../styles/adsLanding.css";
import { useEffect, useState } from "react";
import Select from "react-select";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { POSITION, AREA } from "../utils/constants";
import { captureAttribution, getAttribution } from "../utils/attribution";

/* ── Retailer trust strip (subset — keep the page light) ────────── */
const TRUST_LOGOS = [
  { name: "Walmart MX", src: "/images/logo_wm_mx.png" },
  { name: "Chedraui", src: "/images/logo_chedraui.png" },
  { name: "Soriana", src: "/images/logo_soriana.png" },
  { name: "La Comer", src: "/images/logo_lacomer.jpg" },
  { name: "Amazon MX", src: "/images/logo_amazon_mx.png" },
  { name: "Mercado Libre", src: "/images/logo_mercado_libre.jpg" },
];

const initialData = {
  name: "",
  company: "",
  email: "",
  phone: null,
  position: null,
  area: null,
};

/** Fires GA4 + Google Ads conversion events if gtag is loaded on the page.
 *  Safe no-op if gtag hasn't been configured yet (see public/index.html). */
function fireConversionEvents() {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("event", "generate_lead", {
    event_category: "ads_landing",
    event_label: "demo_request",
  });
  // Conversion action: "Envío de formulario para clientes potenciales"
  window.gtag("event", "conversion", {
    send_to: "AW-18343132913/lT1gCNvO_9QcEPH916pE",
    value: 1.0,
    currency: "USD",
  });
}

function AdsLanding({ t, number }) {
  const [status, setStatus] = useState("none");
  const [formData, setFormData] = useState(initialData);

  useEffect(() => {
    captureAttribution();
  }, []);

  const handleSelectChange = (label) => (opt) =>
    setFormData((prev) => ({ ...prev, [label]: opt ? opt.value : null }));

  const handlePhoneChange = (val) =>
    setFormData((prev) => ({ ...prev, phone: val }));

  const handleTextChange = (field) => (e) =>
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  async function onSubmit(event) {
    event.preventDefault();
    if (
      !formData.name ||
      !formData.company ||
      !formData.email ||
      !formData.phone ||
      !formData.position ||
      !formData.area
    ) {
      return;
    }

    const attribution = getAttribution();
    // Keep the payload shape identical to the original, already-working
    // form (name, company, email, phone, position, area, type_register).
    // Campaign attribution rides along inside type_register itself,
    // rather than as new top-level fields the backend may not expect —
    // the earlier version sent utm_source/gclid/etc. as separate keys,
    // which a strict backend can reject outright as unrecognized fields.
    const attributionNote = Object.keys(attribution).length
      ? ` | utm_source=${attribution.utm_source || "-"}` +
        ` utm_medium=${attribution.utm_medium || "-"}` +
        ` utm_campaign=${attribution.utm_campaign || "-"}` +
        ` gclid=${attribution.gclid ? "yes" : "no"}`
      : "";
    const payload = {
      ...formData,
      type_register: `${t("landing_form_title")}${attributionNote}`,
    };

    setStatus("sending");
    try {
      const res = await fetch("https://api2.shelf-e.com:9080/api/form", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        redirect: "follow",
      });
      if (!res.ok) {
        setStatus("error");
      } else {
        await res.json();
        setStatus("success");
        fireConversionEvents();
      }
    } catch (e) {
      setStatus("error");
    }
  }

  return (
    <div className="lp-page">
      {/* ── Minimal top bar: logo only, nothing to click away to ── */}
      <div className="lp-topbar">
        <div className="lp-logo">
          <img src="/images/LogoShelf-E.png" alt="Shelf-e" />
        </div>
      </div>

      {/* ── Hero + form ─────────────────────────────────────────── */}
      <section className="lp-hero">
        <div className="lp-hero-inner">
          <div className="lp-copy">
            <p className="lp-eyebrow">{t("landing_eyebrow")}</p>
            <h1 className="lp-h1">{t("landing_h1")}</h1>
            <p className="lp-sub">{t("landing_sub")}</p>

            <ul className="lp-bullets">
              {["landing_bullet_1", "landing_bullet_2", "landing_bullet_3", "landing_bullet_4"].map(
                (key) => (
                  <li key={key}>
                    <span className="lp-bullet-check">✓</span>
                    <span>{t(key)}</span>
                  </li>
                )
              )}
            </ul>

            <p className="lp-footnote">{t("hero_footnote")}</p>
          </div>

          <div className="lp-form-card">
            {status === "none" && (
              <>
                <p className="lp-form-title">{t("landing_form_title")}</p>
                <p className="lp-form-sub">{t("landing_form_sub")}</p>
                <form onSubmit={onSubmit}>
                  <div className="lp-field">
                    <label htmlFor="lp-name">{t("form_2")}</label>
                    <input
                      id="lp-name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleTextChange("name")}
                    />
                  </div>
                  <div className="lp-field">
                    <label htmlFor="lp-company">{t("form_3")}</label>
                    <input
                      id="lp-company"
                      type="text"
                      required
                      value={formData.company}
                      onChange={handleTextChange("company")}
                    />
                  </div>
                  <div className="lp-field">
                    <label htmlFor="lp-email">{t("form_4")}</label>
                    <input
                      id="lp-email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleTextChange("email")}
                    />
                  </div>
                  <div className="lp-field">
                    <label htmlFor="lp-phone">{t("form_5")}</label>
                    <PhoneInput
                      inputProps={{ id: "lp-phone", required: true }}
                      inputStyle={{ width: "100%" }}
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      country="mx"
                    />
                  </div>
                  <div className="lp-field">
                    <label htmlFor="lp-position">{t("form_6")}</label>
                    <Select
                      inputId="lp-position"
                      menuPosition="fixed"
                      isSearchable
                      options={POSITION[number]}
                      placeholder={t("form_7")}
                      onChange={handleSelectChange("position")}
                    />
                  </div>
                  <div className="lp-field">
                    <label htmlFor="lp-area">{t("form_8")}</label>
                    <Select
                      inputId="lp-area"
                      menuPosition="fixed"
                      isSearchable
                      options={AREA[number]}
                      placeholder={t("form_7")}
                      onChange={handleSelectChange("area")}
                    />
                  </div>
                  <button type="submit" className="lp-submit">
                    {t("landing_cta_button")}
                  </button>
                  <p className="lp-privacy">{t("landing_privacy")}</p>
                </form>
              </>
            )}

            {status === "sending" && (
              <div className="lp-status">
                <img src="/images/loading.gif" alt="" />
                <h4>{t("form_9")}</h4>
              </div>
            )}

            {status === "error" && (
              <div className="lp-status">
                <img src="/images/warning.svg" alt="" />
                <h4>{t("form_10")}</h4>
                <p>{t("form_11")}</p>
              </div>
            )}

            {status === "success" && (
              <div className="lp-status">
                <img src="/images/success.svg" alt="" />
                <h4>{t("form_12")}</h4>
                <p>{t("form_13")}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Trust strip ─────────────────────────────────────────── */}
      <section className="lp-trust">
        <div className="lp-trust-inner">
          <p className="lp-trust-label">{t("landing_trust_label")}</p>
          <div className="lp-trust-logos">
            {TRUST_LOGOS.map((r) => (
              <img key={r.name} src={r.src} alt={r.name} title={r.name} />
            ))}
          </div>
        </div>
      </section>

      <footer className="lp-footer">© {new Date().getFullYear()} Shelf-e · shelf-e.com</footer>
    </div>
  );
}

export default AdsLanding;
